<template>
  <div class="campo-timestamp-container mb-3">
    <label
      v-if="label && !hideLabel"
      :for="id"
      class="form-label"
      :class="{ 'is-required': isObrigatorio }"
    >
      {{ label }}
    </label>

    <div class="input-group has-validation">
      <input
        :id="id"
        type="text"
        class="form-control input-readonly-clickable"
        :class="{ 'is-invalid': !!errorMessage }"
        :value="textoExibicao"
        readonly
        :disabled="disabled"
        placeholder="Selecione data e hora..."
        @click="!disabled && abrir()"
      >
      <button
        class="btn btn-outline-secondary d-flex align-items-center"
        type="button"
        :class="{ 'border-danger text-danger': !!errorMessage }"
        :disabled="disabled"
        @click="abrir"
      >
        <i class="bi bi-calendar-event"></i>
      </button>
      <button
        v-if="textoExibicao && !disabled"
        class="btn btn-outline-secondary d-flex align-items-center"
        type="button"
        title="Limpar"
        @click="limpar"
      >
        <i class="bi bi-x-lg"></i>
      </button>

      <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
    </div>

    <!-- Overlay: calendário + seleção de hora (relógio circular / digitação) -->
    <Teleport to="body">
      <div v-if="aberto" class="cts-overlay" :style="{ zIndex: zIndexEfetivo }" @click.self="fechar">
        <div class="cts-panel">
          <header class="cts-header">
            <i class="bi bi-calendar-event me-2"></i>
            <span class="cts-title">Selecione a data e a hora</span>
            <button type="button" class="btn-close btn-close-white ms-auto" @click="fechar"></button>
          </header>

          <div class="cts-body">
            <!-- Breadcrumb de etapas -->
            <div class="cts-steps">
              <button
                type="button"
                class="cts-step"
                :class="{ 'cts-step-ativo': etapa === 'data' }"
                @click="irPara('data')"
              >
                <i class="bi bi-calendar3 me-1"></i>{{ dataResumo }}
              </button>
              <button
                type="button"
                class="cts-step"
                :class="{ 'cts-step-ativo': etapa === 'hora' }"
                @click="irPara('hora')"
              >
                {{ pad(hora) }}h
              </button>
              <button
                v-if="nivel !== 'hora'"
                type="button"
                class="cts-step"
                :class="{ 'cts-step-ativo': etapa === 'minuto' }"
                @click="irPara('minuto')"
              >
                {{ pad(minuto) }}m
              </button>
              <button
                v-if="nivel === 'segundo'"
                type="button"
                class="cts-step"
                :class="{ 'cts-step-ativo': etapa === 'segundo' }"
                @click="irPara('segundo')"
              >
                {{ pad(segundo) }}s
              </button>
            </div>

            <!-- Etapa: data -->
            <div v-show="etapa === 'data'" class="cts-etapa-data">
              <VueDatePicker
                v-if="!modoDigitacao"
                :model-value="dataSelecionada"
                :inline="true"
                :enable-time-picker="false"
                :auto-apply="true"
                :min-date="dataMinima"
                :max-date="dataMaxima"
                :dark="isDarkMode"
                locale="pt-BR"
                :disabled="disabled"
                @update:model-value="onDataSelecionada"
              />
              <div v-else class="cts-etapa-data-digitacao">
                <div class="cts-etapa-digitacao">
                  <div class="cts-campo">
                    <label class="form-label">Dia</label>
                    <input
                      type="text"
                      inputmode="numeric"
                      maxlength="2"
                      class="form-control text-center"
                      :value="diaDigitado"
                      @input="onInputData('dia', $event)"
                    >
                  </div>
                  <div class="cts-campo">
                    <label class="form-label">Mês</label>
                    <input
                      type="text"
                      inputmode="numeric"
                      maxlength="2"
                      class="form-control text-center"
                      :value="mesDigitado"
                      @input="onInputData('mes', $event)"
                    >
                  </div>
                  <div class="cts-campo">
                    <label class="form-label">Ano</label>
                    <input
                      type="text"
                      inputmode="numeric"
                      maxlength="4"
                      class="form-control text-center"
                      :value="anoDigitado"
                      @input="onInputData('ano', $event)"
                    >
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-info text-white fw-bold"
                  :disabled="!dataDigitadaValida"
                  @click="confirmarDataDigitada"
                >
                  OK
                </button>
              </div>
            </div>

            <!-- Etapa: relógio circular -->
            <div v-if="etapa !== 'data' && !modoDigitacao" class="cts-etapa-relogio">
              <RelogioCircular
                :model-value="valorEtapa"
                :tipo="etapa"
                :modo-smartphone="!isDesktop"
                @update:model-value="onRelogioUpdate"
                @selecionar="onRelogioSelecionar"
              />
            </div>

            <!-- Etapa: digitação -->
            <div v-if="etapa !== 'data' && modoDigitacao" class="cts-etapa-digitacao">
              <div class="cts-campo">
                <label class="form-label">Hora</label>
                <input
                  type="text"
                  inputmode="numeric"
                  maxlength="2"
                  class="form-control text-center"
                  :value="pad(hora)"
                  @input="onInputTempo('hora', $event)"
                >
              </div>
              <div v-if="nivel !== 'hora'" class="cts-campo">
                <label class="form-label">Minuto</label>
                <input
                  type="text"
                  inputmode="numeric"
                  maxlength="2"
                  class="form-control text-center"
                  :value="pad(minuto)"
                  @input="onInputTempo('minuto', $event)"
                >
              </div>
              <div v-if="nivel === 'segundo'" class="cts-campo">
                <label class="form-label">Segundo</label>
                <input
                  type="text"
                  inputmode="numeric"
                  maxlength="2"
                  class="form-control text-center"
                  :value="pad(segundo)"
                  @input="onInputTempo('segundo', $event)"
                >
              </div>
            </div>
          </div>

          <footer class="cts-footer">
            <button
              type="button"
              class="btn btn-outline-secondary cts-btn-teclado"
              :title="tituloToggle"
              @click="alternarModoDigitacao"
            >
              <i :class="iconeToggle"></i>
            </button>
            <div class="ms-auto d-flex gap-2">
              <button type="button" class="btn btn-outline-secondary" @click="fechar">Cancelar</button>
              <button
                type="button"
                class="btn btn-info text-white fw-bold"
                :disabled="!dataSelecionada"
                @click="confirmar"
              >
                Confirmar
              </button>
            </div>
          </footer>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import RelogioCircular from './RelogioCircular.vue'
import { componentesBrasil, brasilParaUtcISO, formatarTimestampBR, ehValorMaximoUTC, VALOR_MAXIMO_UTC } from '@/utils/useDataBR'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  // Timestamp UTC ISO (ex: '2026-10-05T17:30:00Z')
  modelValue: { type: String, default: null },
  // Nível de precisão da hora: 'hora' | 'minuto' | 'segundo'
  nivel: {
    type: String,
    default: 'minuto',
    validator: (v) => ['hora', 'minuto', 'segundo'].includes(v)
  },
  required: { type: [Boolean, String, Number], default: false },
  disabled: { type: Boolean, default: false },
  hideLabel: { type: Boolean, default: false },
  error: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

const formErrors = inject('formErrors', ref({}))
const errorMessage = computed(() => props.error || formErrors.value[props.id] || '')

// Modo smartphone: fornecido pelo MainDashboard (window.innerWidth < 992)
const isDesktop = inject('isDesktop', ref(true))

const isObrigatorio = computed(() => {
  return props.required === true || props.required === 'true' || props.required === 'required' || props.required === ''
})

const aberto = ref(false)
const etapa = ref('data')
const modoDigitacao = ref(false)
const dataSelecionada = ref(null)
const hora = ref(0)
const minuto = ref(0)
const segundo = ref(0)
const isDarkMode = ref(false)
const diaDigitado = ref('')
const mesDigitado = ref('')
const anoDigitado = ref('')

// Limites de data. O máximo usa o maior ano de 4 dígitos (9999) para representar
// "vigência sem fim". Anos > 9999 geram ISO com ano expandido (ex: "+010000-..."),
// que o System.Text.Json não desserializa — por isso o clamp no confirmar().
const dataMinima = new Date(1900, 0, 1)
const dataMaxima = new Date(9999, 11, 31)

const pad = (n) => String(n).padStart(2, '0')

const textoExibicao = computed(() => formatarTimestampBR(props.modelValue, props.nivel))

const dataResumo = computed(() => {
  if (!dataSelecionada.value) return 'Data'
  const d = dataSelecionada.value
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
})

const valorEtapa = computed(() => {
  if (etapa.value === 'hora') return hora.value
  if (etapa.value === 'minuto') return minuto.value
  if (etapa.value === 'segundo') return segundo.value
  return 0
})

const iconeToggle = computed(() => {
  if (modoDigitacao.value) {
    return etapa.value === 'data' ? 'bi bi-calendar3' : 'bi bi-clock'
  }
  return 'bi bi-keyboard'
})

const tituloToggle = computed(() => (modoDigitacao.value ? 'Voltar à seleção visual' : 'Digitar valores'))

const sincronizarDoModel = () => {
  if (ehValorMaximoUTC(props.modelValue)) {
    dataSelecionada.value = new Date(9999, 11, 31)
    hora.value = 23
    minuto.value = 59
    segundo.value = 59
    return
  }
  const c = componentesBrasil(props.modelValue)
  if (c) {
    dataSelecionada.value = new Date(c.ano, c.mes - 1, c.dia)
    hora.value = c.hora
    minuto.value = c.minuto
    segundo.value = c.segundo
  } else {
    dataSelecionada.value = null
    hora.value = 0
    minuto.value = 0
    segundo.value = 0
  }
}

const abrir = () => {
  if (props.disabled) return
  const { token, zIndex: z } = reservarZIndex()
  tokenZIndex.value = token
  zIndexReservado.value = z
  sincronizarDoModel()
  inicializarCamposData()
  etapa.value = 'data'
  modoDigitacao.value = false
  aberto.value = true
}

const fechar = () => {
  aberto.value = false
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
}

const limpar = () => {
  emit('update:modelValue', null)
}

const irPara = (destino) => {
  if (destino === 'data') {
    etapa.value = 'data'
    return
  }
  if (!dataSelecionada.value) return
  etapa.value = destino
}

const avancarEtapa = () => {
  if (etapa.value === 'data') {
    etapa.value = 'hora'
    return
  }
  if (etapa.value === 'hora' && props.nivel !== 'hora') {
    etapa.value = 'minuto'
    return
  }
  if (etapa.value === 'minuto' && props.nivel === 'segundo') {
    etapa.value = 'segundo'
  }
}

const onDataSelecionada = (val) => {
  if (!val) return
  dataSelecionada.value = val
  if (etapa.value === 'data') etapa.value = 'hora'
}

const onRelogioUpdate = (val) => {
  if (etapa.value === 'hora') hora.value = val
  else if (etapa.value === 'minuto') minuto.value = val
  else if (etapa.value === 'segundo') segundo.value = val
}

const onRelogioSelecionar = () => {
  avancarEtapa()
}

const onInputTempo = (campo, event) => {
  const digitos = String(event.target.value).replace(/\D/g, '').slice(0, 2)
  const max = campo === 'hora' ? 23 : 59
  let valor = digitos === '' ? 0 : Number(digitos)
  if (valor > max) valor = max
  if (campo === 'hora') hora.value = valor
  else if (campo === 'minuto') minuto.value = valor
  else segundo.value = valor
  event.target.value = pad(valor)
}

const inicializarCamposData = () => {
  const base = dataSelecionada.value || new Date()
  diaDigitado.value = pad(base.getDate())
  mesDigitado.value = pad(base.getMonth() + 1)
  anoDigitado.value = String(base.getFullYear())
}

const dataDigitadaValida = computed(() => {
  const d = Number(diaDigitado.value)
  const m = Number(mesDigitado.value)
  const a = Number(anoDigitado.value)
  if (!d || !m || anoDigitado.value.length < 4) return false
  const data = new Date(a, m - 1, d)
  // Rejeita datas inexistentes (ex: 31/02 vira 03/03)
  return data.getFullYear() === a && data.getMonth() === m - 1 && data.getDate() === d
})

const aplicarDataDigitada = () => {
  if (!dataDigitadaValida.value) return false
  const d = Number(diaDigitado.value)
  const m = Number(mesDigitado.value)
  const a = Number(anoDigitado.value)
  dataSelecionada.value = new Date(a, m - 1, d)
  return true
}

const confirmarDataDigitada = () => {
  if (aplicarDataDigitada()) {
    etapa.value = 'hora'
  }
}

const onInputData = (campo, event) => {
  const maxLen = campo === 'ano' ? 4 : 2
  let digitos = String(event.target.value).replace(/\D/g, '').slice(0, maxLen)
  if (campo === 'dia') {
    if (digitos !== '' && Number(digitos) > 31) digitos = '31'
    diaDigitado.value = digitos
  } else if (campo === 'mes') {
    if (digitos !== '' && Number(digitos) > 12) digitos = '12'
    mesDigitado.value = digitos
  } else {
    anoDigitado.value = digitos
  }
  event.target.value = digitos
  aplicarDataDigitada()
}

const alternarModoDigitacao = () => {
  modoDigitacao.value = !modoDigitacao.value
  if (modoDigitacao.value && etapa.value === 'data') {
    inicializarCamposData()
  }
}

const confirmar = () => {
  if (!dataSelecionada.value) return
  const d = dataSelecionada.value
  const utcISO = brasilParaUtcISO({
    ano: d.getFullYear(),
    mes: d.getMonth() + 1,
    dia: d.getDate(),
    hora: hora.value,
    minuto: props.nivel === 'hora' ? 0 : minuto.value,
    segundo: props.nivel === 'segundo' ? segundo.value : 0
  })
  // Clamp: 9999-12-31 23:59 BRT vira 10000-01-01 UTC (ano expandido, inválido
  // para o System.Text.Json). Fixa no máximo representável em 4 dígitos.
  const dt = new Date(utcISO)
  const valorFinal = dt.getUTCFullYear() > 9999 ? VALOR_MAXIMO_UTC : utcISO
  emit('update:modelValue', valorFinal)
  aberto.value = false
}

const onKeydown = (e) => {
  if (e.key === 'Escape' && aberto.value) fechar()
}

onMounted(() => {
  const htmlEl = document.documentElement
  isDarkMode.value = htmlEl.getAttribute('data-bs-theme') === 'dark'
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})
</script>

<style scoped>
.form-label { font-weight: 400 !important; }
.form-label.is-required::after { content: " *"; color: var(--bs-danger); font-weight: bold; }
.campo-timestamp-container { display: flex; flex-direction: column; }
.input-readonly-clickable {
  background-color: var(--bs-body-bg) !important;
  cursor: pointer;
}

.cts-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cts-panel {
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 340px;
  max-width: 95vw;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header no padrão do sistema (ModalFormulario.vue) */
.cts-header {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: #34495E;
  color: #FFFFFF;
  font-weight: 500;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
}

.cts-title {
  font-size: 1.1rem;
}

.cts-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
  flex-grow: 1;
}

/* Breadcrumb de etapas */
.cts-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
}

.cts-step {
  border: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cts-step:hover {
  border-color: var(--bs-info);
}

.cts-step-ativo {
  background-color: var(--bs-info);
  border-color: var(--bs-info);
  color: #fff;
}

/* Etapa: calendário integrado ao modal (ocupa toda a área e centralizado) */
.cts-etapa-data {
  display: flex;
  justify-content: center;
}

/* Remapeia as variáveis de tema do datepicker para as do sistema.
   Isso corrige o offset (meses adjacentes), hover, selecionado e "hoje"
   sem sobrescrever classes internas. */
.cts-etapa-data :deep(.dp__theme_light),
.cts-etapa-data :deep(.dp__theme_dark) {
  --dp-background-color: var(--bs-body-bg);
  --dp-text-color: var(--bs-body-color);
  --dp-secondary-color: var(--bs-secondary-color);
  --dp-hover-color: var(--bs-tertiary-bg);
  --dp-hover-text-color: var(--bs-body-color);
  --dp-hover-icon-color: var(--bs-body-color);
  --dp-primary-color: var(--bs-info);
  --dp-primary-text-color: #fff;
  --dp-border-color: var(--bs-border-color);
  --dp-border-color-hover: var(--bs-info);
  --dp-menu-border-color: transparent;
  --dp-icon-color: var(--bs-body-color);
  --dp-disabled-color: var(--bs-secondary-color);
  --dp-highlight-color: var(--bs-info);
}

/* Coloca o menu inline no fluxo normal e o estica para centralizar */
.cts-etapa-data :deep(.dp__main) {
  display: block;
  width: 100%;
}

.cts-etapa-data :deep(.dp__menu) {
  position: relative;
  width: 100%;
  min-width: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

.cts-etapa-data :deep(.dp__menu_inner) {
  padding: 0;
}

.cts-etapa-data :deep(.dp__calendar) {
  width: 100%;
}

.cts-etapa-data :deep(.dp__calendar_header_item) {
  color: var(--bs-secondary-color);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
}

.cts-etapa-data :deep(.dp__month_year_select) {
  color: var(--bs-body-color);
  font-weight: 600;
}

.cts-etapa-data :deep(.dp__cell_inner) {
  border-radius: 50%;
}

/* Etapa: relógio */
.cts-etapa-relogio {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

/* Etapa: digitação */
.cts-etapa-digitacao {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  padding: 1.5rem 0;
}

/* Digitação de data: campos + botão OK */
.cts-etapa-data-digitacao {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.cts-etapa-data-digitacao .cts-etapa-digitacao {
  padding: 0.5rem 0 0;
}

.cts-campo {
  flex: 1;
  max-width: 110px;
}

.cts-campo .form-label {
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  color: var(--bs-secondary-color);
  text-align: center;
}

.cts-campo .form-control {
  font-size: 1.5rem;
  font-weight: 600;
}

.cts-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: var(--bs-tertiary-bg);
  border-top: 1px solid var(--bs-border-color-translucent);
  flex-shrink: 0;
}

.cts-btn-teclado {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 38px;
}
</style>
